import {
  Box,
  Flex,
  List,
  Loader,
  MantineTheme,
  ScrollArea,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useContext, useEffect, useState } from "react";
import { FiExternalLink } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { AppContext } from "src/api/AppContext";
import {
  AttendanceByClientQuery,
  useAttendanceByClientLazyQuery,
} from "src/generated/graphql";
import { CLIENT_ROUTES, ClientCardBg } from "src/utils/constants";

export type IEvents = AttendanceByClientQuery["attendanceByClientUpcoming"];

const ScheduleItem = (
  meeting: any,
  theme: MantineTheme,
  onClick: () => void
) => {
  const startTime = meeting?.startAt?.split("T")[1].slice(0, 5);
  const endTime = meeting?.endAt?.split("T")[1].slice(0, 5);
  return (
    <List.Item
      key={meeting.id}
      my={15}
      py={10}
      style={{ background: theme.colors.primary[6], border: "none" }}
      onClick={() => onClick()}
    >
      <Flex align={"center"}>
        <Flex
          direction={"column"}
          justify={"center"}
          align={"center"}
          style={{
            borderRadius: "50px",
            padding: "5px 10px",
            backgroundColor: theme.colors.primary[0],
            color: theme.colors.primaryTextColor[6],
          }}
        >
          <Text size={14} fw={400}>
            {startTime}
          </Text>
          <Box
            sx={{
              borderBottom: `1px solid ${theme.colors.primaryTextColor[6]}`,
              height: 1,
              width: 20,
            }}
          ></Box>
          <Text size={14} fw={400}>
            {endTime}
          </Text>
        </Flex>
        <Flex
          direction={"column"}
          ml={10}
          style={{ color: theme.colors.primaryTextColor[6] }}
        >
          <Text size={14} fw={500}>
            {meeting.name}
          </Text>
          <Text
            size={14}
            fw={400}
          >{`${meeting.room.name}, ${meeting.location.name}`}</Text>
        </Flex>
      </Flex>
    </List.Item>
  );
};

const MySchedule = () => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { currentClient } = useContext(AppContext);
  const [getAttendance, { data, loading }] = useAttendanceByClientLazyQuery();
  const [today, setToday] = useState<IEvents>([]);
  const [tomorrow, setTomorrow] = useState<IEvents>([]);
  const [upcoming, setUpcoming] = useState<IEvents>([]);

  useEffect(() => {
    currentClient?.id &&
      getAttendance({ variables: { clientId: currentClient.id } });
  }, [currentClient]);

  useEffect(() => {
    if (!loading && data && data.attendanceByClientUpcoming) {
      sortEvents(data.attendanceByClientUpcoming);
    }
  }, [data, loading]);

  const sortEvents = (events: IEvents) => {
    let todayEvents = events.filter(({ meeting }) => {
      const { startAt } = meeting;
      const isToday =
        new Date(startAt).toDateString() === new Date().toDateString();
      return isToday;
    });
    setToday(todayEvents);

    let tomorrowEvents = events.filter(({ meeting }) => {
      const { startAt } = meeting;
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const isTommorrow =
        new Date(startAt).toDateString() === new Date(tomorrow).toDateString();
      return isTommorrow;
    });
    setTomorrow(tomorrowEvents);

    let upcomingEvents = events.filter(({ meeting }) => {
      const { startAt } = meeting;
      const isToday =
        new Date(startAt).toDateString() === new Date().toDateString();
      return isToday;
    });
    setUpcoming(upcomingEvents);
  };

  return (
    <Flex
      direction={"column"}
      w={"100%"}
      my={20}
      bg={ClientCardBg}
      style={{ borderRadius: "10px" }}
    >
      <Flex
        w={"100%"}
        justify={"space-between"}
        align={"center"}
        px={10}
        py={15}
      >
        <Text size={18} fw={500}>
          My Schedule
        </Text>
        <FiExternalLink
          size={20}
          onClick={() => navigate(`/${CLIENT_ROUTES.MY_CALENDAR}`)}
        />
      </Flex>
      <ScrollArea w={"98%"} mah={380} type="scroll" scrollbarSize={2}>
        {loading ? (
          <Loader />
        ) : today.length > 0 || tomorrow.length > 0 || upcoming.length > 0 ? (
          <Flex py={10} px={10}>
            {today.length > 0 && (
              <Flex w={280} direction={"column"} mr={10}>
                <Text
                  p={10}
                  size={14}
                  fw={600}
                  style={{ borderRadius: "5px" }}
                  bg={theme.colors.primary[6]}
                  color={theme.colors.primaryTextColor[6]}
                >
                  Today
                </Text>
                <List
                  my={10}
                  styles={{
                    itemWrapper: {
                      width: "100%",
                    },
                    item: {
                      listStyle: "none",
                      cursor: "pointer",
                      border: `1px solid black`,
                      borderRadius: "5px",
                      padding: "5px 10px",
                      bg: theme.colors.primary[6],
                    },
                  }}
                >
                  {today.map(({ meeting }: any) =>
                    ScheduleItem(meeting, theme, () =>
                      navigate(`my_calendar/${meeting.id}`)
                    )
                  )}
                </List>
              </Flex>
            )}

            {tomorrow.length > 0 && (
              <Flex w={280} direction={"column"} mr={10}>
                <Text
                  p={10}
                  size={14}
                  fw={600}
                  style={{ borderRadius: "5px" }}
                  bg={theme.colors.primary[6]}
                  color={theme.colors.primaryTextColor[6]}
                >
                  Tommorrow
                </Text>
                <List
                  my={10}
                  styles={{
                    itemWrapper: {
                      width: "100%",
                    },
                    item: {
                      listStyle: "none",
                      cursor: "pointer",
                      border: `1px solid black`,
                      borderRadius: "5px",
                      padding: "15px",
                      bg: theme.colors.primary[6],
                    },
                  }}
                >
                  {tomorrow.map(({ meeting }: any) =>
                    ScheduleItem(meeting, theme, () =>
                      navigate(`my_calendar/${meeting.id}`)
                    )
                  )}
                </List>
              </Flex>
            )}

            {upcoming.length > 0 && (
              <Flex w={280} direction={"column"}>
                <Text
                  p={10}
                  size={14}
                  fw={600}
                  style={{ borderRadius: "5px" }}
                  bg={theme.colors.primary[6]}
                  color={theme.colors.primaryTextColor[6]}
                >
                  Upcoming
                </Text>
                <List
                  my={10}
                  styles={{
                    itemWrapper: {
                      width: "100%",
                    },
                    item: {
                      listStyle: "none",
                      cursor: "pointer",
                      border: `1px solid black`,
                      borderRadius: "5px",
                      padding: "15px",
                      bg: theme.colors.primary[6],
                    },
                  }}
                >
                  {upcoming.map(({ meeting }: any) =>
                    ScheduleItem(meeting, theme, () =>
                      navigate(`my_calendar/${meeting.id}`)
                    )
                  )}
                </List>
              </Flex>
            )}
          </Flex>
        ) : (
          <Flex px={15} py={10} direction={"column"}>
            <Text
              p={10}
              size={14}
              fw={600}
              style={{ borderRadius: "5px" }}
              bg={theme.colors.primary[6]}
              color={theme.colors.primaryTextColor[6]}
            >
              No scheduled events
            </Text>
          </Flex>
        )}
      </ScrollArea>
    </Flex>
  );
};

export default MySchedule;
